import { useEffect, useState } from 'react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Textarea } from 'components/ui/textarea'

import * as presentationService from '../services/presentationService'
import type { DeckDefinition, SavedDeckTemplate } from '../types/presentation.types'

/**
 * Save a whole deck as a template (MYD-604), from the editor's menu. The
 * pattern of SaveTemplateDialog, for a deck rather than one story.
 *
 * The server reads the SAVED deck, so the editor saves first. The first
 * story's asset decides what the template is for; each later story is kept as
 * that asset itself, or as a story per asset reached from it (its location,
 * its locations, its projects). What it could not keep comes back as warnings.
 */
interface SaveDeckTemplateDialogProps {
	open: boolean
	presentationId: string
	deckName: string
	deck: DeckDefinition
	onClose: () => void
	onSaved: (result: SavedDeckTemplate) => void
}

export function SaveDeckTemplateDialog({ open, presentationId, deckName, deck, onClose, onSaved }: SaveDeckTemplateDialogProps) {
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!open) return
		setName(deckName ? `${deckName} template` : '')
		setDescription('')
		setError(null)
	}, [open, deckName])

	const first = deck.stories[0]
	const trimmed = name.trim()
	const blocked = !first
		? 'Add a story first: a template is made from the deck’s stories.'
		: !first.anchor
			? 'Choose what the first story is about: the template is for that kind of asset.'
			: null

	const submit = async () => {
		if (!trimmed || blocked) return
		setBusy(true)
		setError(null)
		try {
			onSaved(await presentationService.saveDeckAsTemplate(presentationId, { name: trimmed, description: description.trim() }))
		} catch (cause: any) {
			setError(cause?.response?.data?.message ?? 'The template could not be saved.')
		} finally {
			setBusy(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[480px]">
				<DialogHeader>
					<DialogTitle>Save deck as template</DialogTitle>
					<DialogDescription>
						Makes this deck again for any {first?.anchor?.scope ?? 'asset'}, with its stories bound to that one. Only you see it.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 px-5 py-4">
					{blocked ? (
						<p className="text-sm text-warning">{blocked}</p>
					) : (
						<>
							<div className="space-y-2">
								<Label htmlFor="deck-template-name">Name</Label>
								<Input id="deck-template-name" value={name} maxLength={140} autoFocus onChange={event => setName(event.target.value)} />
							</div>
							<div className="space-y-2">
								<Label htmlFor="deck-template-description">
									Description <span className="font-normal text-muted-foreground">(optional)</span>
								</Label>
								<Textarea id="deck-template-description" rows={2} value={description} maxLength={500} onChange={event => setDescription(event.target.value)} />
							</div>
						</>
					)}
					{error && <p className="text-sm text-destructive">{error}</p>}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose} disabled={busy}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={busy || !trimmed || Boolean(blocked)}>
						{busy ? 'Saving...' : 'Save template'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default SaveDeckTemplateDialog
